// png.ts — the BROWSER twin of test/harness/png.mjs (the proven minimal PNG
// decoder): same chunk walk, same filter reconstruction, same RGBA expansion,
// with the node-only pieces swapped for browser-safe ones — node:zlib
// inflateSync -> fflate's unzlibSync (the IDAT payload is a zlib stream), and
// Buffer reads -> Uint8Array/DataView. PNG decode is lossless, so any correct
// decoder yields identical RGBA (the M2 static-frame oracle proved the
// pipeline end to end against the real client's pixels).
//
// TexAtlasMgr.Init wants a SYNC decode callback; the browser's native decoders
// (Image/createImageBitmap) are async, hence this pure-JS path. Supports what
// the shipped atlas/font textures use: 8-bit RGB/RGBA/grayscale/palette, all 5
// scanline filters, non-interlaced.

import { unzlibSync } from 'fflate';

const u32 = (b: Uint8Array, o: number): number =>
  ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;

/** Decode a PNG buffer to { cx, cy, rgba: Uint8Array }. */
export function decodePng(buf: Uint8Array): { cx: number; cy: number; rgba: Uint8Array } {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  for (let i = 0; i < 8; i++) {
    if (buf[i] !== sig[i]) throw new Error('not a PNG');
  }
  let cx = 0,
    cy = 0,
    bitDepth = 0,
    colorType = 0,
    interlace = 0;
  let palette: Uint8Array | null = null,
    trns: Uint8Array | null = null;
  const idat: Uint8Array[] = [];
  let cbIdat = 0;
  for (let off = 8; off < buf.length;) {
    const len = u32(buf, off);
    const type = String.fromCharCode(buf[off + 4], buf[off + 5], buf[off + 6], buf[off + 7]);
    const data = buf.subarray(off + 8, off + 8 + len);
    if (type === 'IHDR') {
      cx = u32(data, 0);
      cy = u32(data, 4);
      bitDepth = data[8];
      colorType = data[9];
      interlace = data[12];
    } else if (type === 'PLTE') {
      palette = data;
    } else if (type === 'tRNS') {
      trns = data;
    } else if (type === 'IDAT') {
      idat.push(data);
      cbIdat += data.length;
    } else if (type === 'IEND') {
      break;
    }
    off += 12 + len;
  }
  if (bitDepth !== 8) throw new Error(`unsupported bit depth ${bitDepth}`);
  if (interlace !== 0) throw new Error('interlaced PNG unsupported');
  const channels = ({ 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 } as Record<number, number>)[colorType];
  if (channels === undefined) throw new Error(`unsupported color type ${colorType}`);

  // concat IDAT chunks, then inflate the zlib stream
  const zdata = new Uint8Array(cbIdat);
  for (let i = 0, o = 0; i < idat.length; o += idat[i].length, i++) zdata.set(idat[i], o);
  const raw = unzlibSync(zdata);

  const stride = cx * channels;
  const px = new Uint8Array(cy * stride);

  // filter reconstruction (spec 4.5.2; a=left, b=up, c=upper-left)
  for (let y = 0; y < cy; y++) {
    const filter = raw[y * (stride + 1)];
    const row = y * stride;
    const prev = row - stride;
    const src = y * (stride + 1) + 1;
    for (let x = 0; x < stride; x++) {
      const v = raw[src + x];
      const a = x >= channels ? px[row + x - channels] : 0;
      const b = y > 0 ? px[prev + x] : 0;
      const c = x >= channels && y > 0 ? px[prev + x - channels] : 0;
      let out;
      switch (filter) {
        case 0:
          out = v;
          break;
        case 1:
          out = v + a;
          break;
        case 2:
          out = v + b;
          break;
        case 3:
          out = v + ((a + b) >> 1);
          break;
        case 4: {
          const p = a + b - c;
          const pa = Math.abs(p - a),
            pb = Math.abs(p - b),
            pc = Math.abs(p - c);
          out = v + (pa <= pb && pa <= pc ? a : pb <= pc ? b : c);
          break;
        }
        default:
          throw new Error(`bad filter ${filter} at row ${y}`);
      }
      px[row + x] = out & 0xff;
    }
  }

  // expand to RGBA
  const rgba = new Uint8Array(cx * cy * 4);
  for (let i = 0, o = 0; i < cx * cy; i++, o += 4) {
    const s = i * channels;
    switch (colorType) {
      case 0:
        rgba[o] = rgba[o + 1] = rgba[o + 2] = px[s];
        rgba[o + 3] = 255;
        break;
      case 2:
        rgba[o] = px[s];
        rgba[o + 1] = px[s + 1];
        rgba[o + 2] = px[s + 2];
        rgba[o + 3] = 255;
        break;
      case 3: {
        const p = px[s] * 3;
        rgba[o] = palette![p];
        rgba[o + 1] = palette![p + 1];
        rgba[o + 2] = palette![p + 2];
        rgba[o + 3] = trns !== null && px[s] < trns.length ? trns[px[s]] : 255;
        break;
      }
      case 4:
        rgba[o] = rgba[o + 1] = rgba[o + 2] = px[s];
        rgba[o + 3] = px[s + 1];
        break;
      case 6:
        rgba[o] = px[s];
        rgba[o + 1] = px[s + 1];
        rgba[o + 2] = px[s + 2];
        rgba[o + 3] = px[s + 3];
        break;
    }
  }
  return { cx, cy, rgba };
}
